import { useEffect, useState } from 'react';
import { Input } from 'antd';
import { Button } from 'ui/atoms';
import DOMPurify from 'dompurify';
import classNames from 'classnames';

import {
  countStringNewLines,
  encodeStringToHtmlEntities,
} from 'utils/string-utils';
import { CurrentConversationStore } from 'stores/current-conversation';
import { QUESTION_LENGTH_MAX_IN_CHARS } from 'stores/metadata';
import { UserStore } from 'stores/user';
import { FileChitRow } from 'ui/molecules/FileChitRow';
import {
  diffCurrentPendingFiles,
  FileType,
  filterFilesInProgress,
  sortFilesByLastUpdated,
} from 'utils/file-utils';

import { ReactComponent as QuestionInputIcon } from './submit-button.svg';

import './question-input.css';

const { TextArea } = Input;

type QuestionInputProps = {
  onSubmit: (question: string) => void;
  value?: string;
};

export const QuestionInput: React.FC<QuestionInputProps> = ({
  onSubmit,
  value = '',
}) => {
  const [questionValue, setQuestionValue] = useState(value);
  const [textareaRows, setTextareaRows] = useState(1);
  const [isSubmitable, setIsSubmitable] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [isMultiline, setIsMultiline] = useState(true);
  const isStreaming = CurrentConversationStore.useState((s) => s.isStreaming);
  const isConversationLoading = CurrentConversationStore.useState(
    (s) => s.isLoading,
  );
  const filesSelected = CurrentConversationStore.useState(
    (s) => s.fileContexts,
  );
  const personalFiles = UserStore.useState((s) => s.personalFiles);
  const [pendingFilesToApply, setPendingFilesToApply] = useState<FileType[]>(
    [],
  );

  useEffect(() => {
    setDisableSubmit(isStreaming || !isSubmitable);
  }, [isSubmitable, isStreaming]);

  useEffect(() => {
    const charactersPerRow = 75;
    const questionLength = questionValue.length;
    const rows = Math.ceil(questionLength / charactersPerRow);
    const hasNewlines = questionValue.includes('\n');

    setTextareaRows(1);
    setIsSubmitable(false);
    setIsMultiline(false);

    if (questionLength) {
      setIsSubmitable(true);
    }

    if (questionLength >= QUESTION_LENGTH_MAX_IN_CHARS) {
      setQuestionValue(questionValue.slice(0, QUESTION_LENGTH_MAX_IN_CHARS));
    }

    if (hasNewlines) {
      const newLines = countStringNewLines(questionValue);
      setIsMultiline(true);
      setTextareaRows((prev) => prev + newLines);
    }

    if (rows > 1 && !hasNewlines) {
      setIsMultiline(true);
      setTextareaRows(rows);
    }
  }, [questionValue]);

  useEffect(() => {
    if (!isMultiline) {
      setTextareaRows(1);
    }
  }, [isMultiline]);

  useEffect(() => {
    if (!isConversationLoading) {
      return;
    }
    setQuestionValue('');
  }, [isConversationLoading]);

  useEffect(() => {
    const processingFiles = filterFilesInProgress(personalFiles);

    if (processingFiles.length && questionValue?.length > 1) {
      setIsSubmitable(false);
    }

    if (!processingFiles.length && questionValue?.length > 1) {
      setIsSubmitable(true);
    }

    const pending = diffCurrentPendingFiles(personalFiles, filesSelected);
    setPendingFilesToApply([...pending, ...processingFiles]);

    // eslint-disable-next-line
  }, [filesSelected, personalFiles]);

  const submit = () => {
    if (!questionValue?.length) {
      return;
    }

    const formatted = encodeStringToHtmlEntities(questionValue);
    onSubmit(DOMPurify.sanitize(formatted));
    setQuestionValue('');

    setPendingFilesToApply([]);
  };

  return (
    <div className="conversation-box">
      <QuestionInputFileChits pendingFilesToApply={pendingFilesToApply} />

      <div
        className={classNames([
          'conversation',
          {
            multiline: isMultiline,
            'has-file-chits': pendingFilesToApply.length,
          },
        ])}
      >
        <TextArea
          placeholder="Ask me anything..."
          value={questionValue}
          rows={textareaRows}
          onChange={(e) => {
            setQuestionValue(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              !disableSubmit && submit();
            }
          }}
          data-testid="question-input-textarea"
        />
        <Button
          type="text"
          shape="circle"
          className={classNames(['', { '-disabled': disableSubmit }])}
          disabled={disableSubmit}
          onClick={submit}
          data-testid="question-input-button"
          icon={<QuestionInputIcon />}
        />
      </div>
      <span
        className={classNames(['input-character-limit'], {
          '-show': questionValue.length > 0,
        })}
        data-testid="character-limit-warning"
      >
        {questionValue.length}/{QUESTION_LENGTH_MAX_IN_CHARS}
      </span>
    </div>
  );
};

export const QuestionInputFileChits = ({ pendingFilesToApply }) => {
  return (
    <div
      className={classNames([
        'conversation-file-chits',
        {
          'has-file-chits': pendingFilesToApply.length,
        },
      ])}
    >
      <FileChitRow
        size="small"
        isEditable={true}
        files={sortFilesByLastUpdated(pendingFilesToApply)}
      />
    </div>
  );
};
